import './App.css';
import { DesignGallery } from './DesignGallery';
import { galleryCategories, localAsset } from './galleryData';

const ORIGIN = 'https://poojasharma.com.np';

const site = {
  name: 'Pooja Sharma',
  roleLine: 'Graphic designer · branding · digital media',
  tagline: 'Crafting bold identities with purpose and personality',
  intro:
    'I am a graphic designer focused on building memorable brand identities and striking digital visuals. With 4+ years of experience across logo design, branding, and digital media, I use Adobe Creative Suite, Figma, and Blender to create work that feels thoughtful, clear, and built to last.',
  contactFormUrl: `${ORIGIN}/contact.php`,
  websiteUrl: ORIGIN,
  designGalleryUrl: `${ORIGIN}/design`,
  cvUrl:
    'https://docs.google.com/document/d/1sQQarnXQ8RylsgvvNSMQbN6QESVh91N6GkKJXp_tTzY/edit?usp=sharing',
  skills: [
    'Adobe Illustrator',
    'Adobe Photoshop',
    'Adobe XD',
    'Adobe InDesign',
    'Figma',
    'Blender',
  ],
  experience: [
    {
      role: 'Graphic Designer',
      place: 'TechGlazers',
      detail: 'Present · logos, advertising posts, brochures',
    },
    {
      role: 'Graphic Designer',
      place: 'SixSigma Educational Consultancy',
      detail: 'Present · advertising posts, brochures',
    },
    {
      role: 'Graphic Designer',
      place: 'AllStar Technology',
      detail: '1 year · logos, advertising posts, brochures',
    },
    {
      role: 'Graphics Designer',
      place: 'Yarsa Labs',
      detail: '6 months · game assets',
    },
    {
      role: 'Graphic Designer',
      place: 'Infinity Sysmetixs',
      detail: '6 months · remote · logos, banners, brochures, cards',
    },
    {
      role: 'Graphics Designer',
      place: 'Gamekunda',
      detail: '1 year · game assets & UI',
    },
  ],
  photos: {
    hero: localAsset('src/images/image.png'),
    heroFloat: localAsset('src/images/img.png'),
    about: [localAsset('src/images/img.png'), localAsset('src/design/logo/gamekunda.png')],
  },
};

function App() {
  return (
    <div className="portfolio">
      <div className="grain" aria-hidden />
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <div className="watermark" aria-hidden>
        DESIGN
      </div>

      <header className="site-header">
        <a className="logo logo-link" href={site.websiteUrl}>
          {site.name}
        </a>
        <nav className="nav" aria-label="Primary">
          <a href="#gallery">Gallery</a>
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </nav>
      </header>

      <main id="main">
        <section className="hero" aria-labelledby="hero-heading">
          <div className="hero-copy">
            <p className="scribble-label">
              {site.roleLine} - {site.tagline}
            </p>
            <h1 id="hero-heading" className="hero-title">
              I design
              <br />
              <span className="hero-title-fill">brands &amp; visuals</span>
              <span className="hero-title-outline"> with meaning and impact.</span>
            </h1>
            <p className="hero-lede">{site.intro}</p>
            <div className="hero-badges">
              <span className="sticker">4+ yrs experience</span>
              <span className="sticker sticker--alt">Adobe CC · Figma · Blender</span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="blob blob--a" aria-hidden />
            <div className="blob blob--b" aria-hidden />
            <figure className="photo-frame photo-frame--hero">
              <img
                src={site.photos.hero}
                alt={`${site.name} showcase`}
                width={900}
                height={1100}
                decoding="async"
              />
              <span className="tape tape--tl" aria-hidden />
              <span className="tape tape--br" aria-hidden />
            </figure>
            <figure className="photo-frame photo-frame--float">
              <img
                src={site.photos.heroFloat}
                alt={`${site.name} profile`}
                width={520}
                height={680}
                decoding="async"
              />
            </figure>
            <p className="float-caption">Welcome to my world of design</p>
          </div>
        </section>

        <section id="gallery" className="section gallery-section" aria-labelledby="gallery-heading">
          <div className="section-head section-head--tilt">
            <h2 id="gallery-heading">Full design gallery</h2>
            <p className="section-sub">
            Everything is designed. Tap an image to enlarge, and videos play inline.
            </p>
          </div>
          <nav className="gallery-toc" aria-label="Gallery categories">
            <ul className="gallery-toc-list">
              {galleryCategories.map((category) => (
                <li key={category.id}>
                  <a href={`#gallery-${category.id}`}>{category.title}</a>
                </li>
              ))}
            </ul>
          </nav>
          <DesignGallery />
        </section>

        <section id="about" className="section about" aria-labelledby="about-heading">
          <div className="section-head">
            <h2 id="about-heading">Skills &amp; qualifications</h2>
            <p className="section-sub">
              A quick overview of my background, creative tools, and professional experience.
            </p>
          </div>

          <div className="about-grid">
            <div className="about-visual">
              <figure className="polaroid polaroid--back">
                <img
                  src={site.photos.about[1]}
                  alt="Logo design sample"
                  width={560}
                  height={720}
                  loading="lazy"
                />
              </figure>
              <figure className="polaroid polaroid--front">
                <img
                  src={site.photos.about[0]}
                  alt={`${site.name} portrait`}
                  width={720}
                  height={920}
                  loading="lazy"
                />
                <figcaption>IT background · immersive visuals</figcaption>
              </figure>
              <span className="doodle doodle--star" aria-hidden />
            </div>
            <div className="about-stack">
              <article className="about-card about-card--qualification">
                <h3 className="about-card-title">Qualifications</h3>
                <p className="about-card-copy">
                  Experienced graphic designer with a background in Information Technology and a
                  passion for creating immersive visual experiences.
                </p>
              </article>

              <article className="about-card about-card--skills">
                <h3 className="about-card-title">Skills</h3>
                <ul className="skill-pills" aria-label="Skills">
                  {site.skills.map((skill) => (
                    <li key={skill}>{skill}</li>
                  ))}
                </ul>
              </article>
            </div>
          </div>

          <article className="about-card about-card--experience">
            <h3 className="about-card-title">Experience</h3>
            <ul className="experience-list">
              {site.experience.map((item) => (
                <li key={`${item.place}-${item.role}`}>
                  <strong>{item.role}</strong> · {item.place}
                  <br />
                  <span className="experience-detail">{item.detail}</span>
                </li>
              ))}
            </ul>
          </article>
        </section>

        <section id="contact" className="section contact" aria-labelledby="contact-heading">
          <div className="contact-panel">
            <h2 id="contact-heading">Let&apos;s work together</h2>
            <p className="contact-lede contact-lede--narrow">
              Send a message through my contact form - I&apos;d love to hear about your project.
            </p>
            <p className="contact-cta-row">
              <a className="email-link" href={site.contactFormUrl}>
                Open contact form
              </a>
            </p>
            <ul className="social">
              <li>
                <a href={site.websiteUrl} target="_blank" rel="noopener noreferrer">
                  Main website ↗
                </a>
              </li>
              <li>
                <a href={site.designGalleryUrl} target="_blank" rel="noopener noreferrer">
                  Original design gallery ↗
                </a>
              </li>
              <li>
                <a href={site.cvUrl} target="_blank" rel="noopener noreferrer">
                  Download CV (Google Doc) ↗
                </a>
              </li>
            </ul>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <p>
          © {new Date().getFullYear()} {site.name} - designer, not decorator.
        </p>
      </footer>
    </div>
  );
}

export default App;
